import { useState, useEffect, useRef } from 'react';
import { adminApi } from './AdminContext';
import { 
  ArrowLeft, Plus, Search, Filter, Phone, Edit2, Trash2, 
  Upload, X, Check, ChevronDown, Download, FileText, Image as ImageIcon,
  User, Briefcase, DollarSign, Star, Clock
} from 'lucide-react';

const LABOR_TYPES = [
  'Aluminium Technician', 'Glass Installer', 'Painter', 
  'Drywall Worker', 'Cleaner', 'Helper'
];

const EXPERIENCE_LEVELS = ['Junior', 'Mid', 'Expert'];
const AVAILABILITY_OPTIONS = ['Available', 'Busy'];

const LaborersModule = ({ onBack, initialAction }) => {
  const [laborers, setLaborers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(initialAction === 'add');
  const [editingLaborer, setEditingLaborer] = useState(null);
  const [selectedLaborer, setSelectedLaborer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    labor_type: '',
    daily_rate: '',
    experience_level: 'Junior',
    availability: 'Available',
    notes: '',
    tags: []
  });

  useEffect(() => {
    loadLaborers();
  }, [searchQuery, filterType]);

  const loadLaborers = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (searchQuery) filters.search = searchQuery;
      if (filterType) filters.labor_type = filterType;
      const data = await adminApi.getLaborers(filters);
      setLaborers(data);
    } catch (error) {
      console.error('Failed to load laborers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        daily_rate: parseFloat(formData.daily_rate) || 0
      };

      if (editingLaborer) {
        await adminApi.updateLaborer(editingLaborer.id, data);
      } else {
        await adminApi.createLaborer(data);
      }

      setShowForm(false);
      setEditingLaborer(null);
      resetForm();
      loadLaborers();
    } catch (error) {
      console.error('Failed to save laborer:', error);
    }
  };

  const handleEdit = (laborer) => {
    setEditingLaborer(laborer);
    setFormData({
      name: laborer.name,
      phone: laborer.phone,
      labor_type: laborer.labor_type,
      daily_rate: laborer.daily_rate.toString(),
      experience_level: laborer.experience_level,
      availability: laborer.availability,
      notes: laborer.notes || '',
      tags: laborer.tags || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this laborer?')) {
      await adminApi.deleteLaborer(id);
      loadLaborers();
      if (selectedLaborer?.id === id) setSelectedLaborer(null);
    }
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file || !selectedLaborer) return;

    setUploading(true);
    try {
      await adminApi.uploadFile(file, 'laborer', selectedLaborer.id, docType);
      const updated = await adminApi.getLaborer(selectedLaborer.id);
      setSelectedLaborer(updated);
      loadLaborers();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      labor_type: '',
      daily_rate: '',
      experience_level: 'Junior',
      availability: 'Available',
      notes: '',
      tags: []
    });
  };

  // Laborer Card Component
  const LaborerCard = ({ laborer }) => (
    <div 
      className="bg-[#0A0F1C] border border-white/10 rounded-lg p-4 cursor-pointer hover:border-white/20"
      onClick={() => setSelectedLaborer(laborer)}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white font-semibold">{laborer.name}</h3>
          <p className="text-slate-400 text-sm">{laborer.labor_type}</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          laborer.availability === 'Available' 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-orange-500/20 text-orange-400'
        }`}>
          {laborer.availability}
        </span>
      </div>
      
      <div className="flex items-center gap-4 text-sm">
        <span className="text-white font-medium">{laborer.daily_rate} AED/day</span>
        <span className="text-slate-400">{laborer.experience_level}</span>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
        <a 
          href={`https://wa.me/${laborer.phone.replace(/[^0-9]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-[#25D366] text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <Phone size={14} />
          {laborer.phone}
        </a>
        <div className="flex gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); handleEdit(laborer); }}
            className="p-2 text-slate-400 hover:text-white"
          >
            <Edit2 size={16} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); handleDelete(laborer.id); }}
            className="p-2 text-slate-400 hover:text-red-400"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  // Detail View
  if (selectedLaborer) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
          <div className="flex items-center gap-3 px-4 py-3">
            <button onClick={() => setSelectedLaborer(null)} className="text-white p-2">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-lg font-bold text-white">Laborer Details</h1>
          </div>
        </header>

        <main className="p-4 pb-24">
          {/* Profile Header */}
          <div className="bg-[#0A0F1C] border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                <User size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{selectedLaborer.name}</h2>
                <p className="text-slate-400">{selectedLaborer.labor_type}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-slate-300">
                <DollarSign size={16} className="text-green-400" />
                <span>{selectedLaborer.daily_rate} AED/day</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Star size={16} className="text-yellow-400" />
                <span>{selectedLaborer.experience_level}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Clock size={16} className={selectedLaborer.availability === 'Available' ? 'text-green-400' : 'text-orange-400'} />
                <span>{selectedLaborer.availability}</span>
              </div>
              <a 
                href={`https://wa.me/${selectedLaborer.phone.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#25D366]"
              >
                <Phone size={16} />
                <span>WhatsApp</span>
              </a>
            </div>

            {selectedLaborer.notes && (
              <p className="mt-4 text-slate-400 text-sm border-t border-white/10 pt-4">
                {selectedLaborer.notes}
              </p>
            )}
          </div>

          {/* Documents Section */}
          <div className="bg-[#0A0F1C] border border-white/10 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-4">Documents</h3>
            
            {/* Upload Buttons */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <label className="flex flex-col items-center gap-1 p-3 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40">
                <Upload size={20} className="text-slate-400" />
                <span className="text-xs text-slate-400">ID Copy</span>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'id_copy')}
                />
              </label>
              <label className="flex flex-col items-center gap-1 p-3 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40">
                <ImageIcon size={20} className="text-slate-400" />
                <span className="text-xs text-slate-400">Photo</span>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFileUpload(e, 'photo')}
                />
              </label>
              <label className="flex flex-col items-center gap-1 p-3 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40">
                <FileText size={20} className="text-slate-400" />
                <span className="text-xs text-slate-400">Certificate</span>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, 'certificate')}
                />
              </label>
            </div>

            {uploading && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin w-6 h-6 border-2 border-[#25D366] border-t-transparent rounded-full"></div>
              </div>
            )}

            {/* Document List */}
            {selectedLaborer.documents?.length > 0 ? (
              <div className="space-y-2">
                {selectedLaborer.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-[#060B14] rounded-lg">
                    <div className="flex items-center gap-3">
                      {doc.content_type?.startsWith('image') ? (
                        <ImageIcon size={20} className="text-blue-400" />
                      ) : (
                        <FileText size={20} className="text-orange-400" />
                      )}
                      <div>
                        <p className="text-white text-sm">{doc.filename}</p>
                        <p className="text-slate-500 text-xs">{doc.doc_type}</p>
                      </div>
                    </div>
                    <a 
                      href={adminApi.getFileUrl(doc.storage_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-[#25D366]"
                    >
                      <Download size={18} />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No documents uploaded</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-4">
            <button 
              onClick={() => handleEdit(selectedLaborer)}
              className="flex-1 py-3 bg-blue-500 text-white font-semibold rounded-lg"
            >
              Edit Laborer
            </button>
            <button 
              onClick={() => handleDelete(selectedLaborer.id)}
              className="px-4 py-3 bg-red-500/20 text-red-400 font-semibold rounded-lg"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Form View
  if (showForm) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
          <div className="flex items-center gap-3 px-4 py-3">
            <button onClick={() => { setShowForm(false); setEditingLaborer(null); resetForm(); }} className="text-white p-2">
              <X size={24} />
            </button>
            <h1 className="text-lg font-bold text-white">
              {editingLaborer ? 'Edit Laborer' : 'Add Laborer'}
            </h1>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-slate-400 text-sm mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Phone *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              placeholder="+971..."
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Type of Labor *</label>
            <select
              value={formData.labor_type}
              onChange={(e) => setFormData({ ...formData, labor_type: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              required
            >
              <option value="">Select type</option>
              {LABOR_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Daily Rate (AED) *</label>
            <input
              type="number"
              value={formData.daily_rate}
              onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
              placeholder="150"
              required
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Experience Level</label>
            <div className="flex gap-2">
              {EXPERIENCE_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, experience_level: level })}
                  className={`flex-1 py-2 rounded-lg border ${
                    formData.experience_level === level
                      ? 'bg-[#25D366] border-[#25D366] text-white'
                      : 'border-white/10 text-slate-400'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Availability</label>
            <div className="flex gap-2">
              {AVAILABILITY_OPTIONS.map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFormData({ ...formData, availability: status })}
                  className={`flex-1 py-2 rounded-lg border ${
                    formData.availability === status
                      ? status === 'Available' 
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'bg-orange-500 border-orange-500 text-white'
                      : 'border-white/10 text-slate-400'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 bg-[#0A0F1C] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366] min-h-[100px]"
              placeholder="Additional notes..."
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-[#25D366] text-white font-semibold rounded-lg"
          >
            {editingLaborer ? 'Update Laborer' : 'Add Laborer'}
          </button>
        </form>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-[#060B14]">
      <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} className="text-white p-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-white">Laborers</h1>
          <button 
            onClick={() => setShowForm(true)}
            className="ml-auto p-2 bg-[#25D366] rounded-lg"
          >
            <Plus size={20} className="text-white" />
          </button>
        </div>

        {/* Search & Filter */}
        <div className="px-4 pb-3 space-y-2">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or phone..."
              className="w-full pl-10 pr-4 py-2 bg-[#060B14] border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-[#25D366]"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setFilterType('')}
              className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                !filterType ? 'bg-[#25D366] text-white' : 'bg-white/10 text-slate-400'
              }`}
            >
              All
            </button>
            {LABOR_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                  filterType === type ? 'bg-[#25D366] text-white' : 'bg-white/10 text-slate-400'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#25D366] border-t-transparent rounded-full"></div>
          </div>
        ) : laborers.length === 0 ? (
          <div className="text-center py-12">
            <User size={48} className="text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No laborers found</p>
            <button 
              onClick={() => setShowForm(true)}
              className="mt-4 px-4 py-2 bg-[#25D366] text-white rounded-lg"
            >
              Add First Laborer
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {laborers.map((laborer) => (
              <LaborerCard key={laborer.id} laborer={laborer} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LaborersModule;
